import { AppBar, Box, Container, List, ListItemButton, Toolbar, Typography, Avatar, Menu, MenuItem, Divider, Stack, IconButton } from '@mui/material';
import { RocketLaunch } from '@mui/icons-material';
import { Outlet, Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import { LoadingContent } from '../../components/Loading';
import { useAuth } from '../../context/AuthContext';
import { getStoredAdvertiser, getStoredUser, isAdvertiserUser, isAdvertiserContractSigned } from '../../services/authSession';
import Logo from '../../assets/logo.png';
import Dropdown from '../../components/Dropdown';
import { useMemo, useState } from 'react';
import {
    canAccessAdvertiserPath,
    canManageAdvertiserCompany,
    canManageAdvertiserOffers,
    canManageAdvertiserUsers,
    canValidateAdvertiserRescues,
    canViewAdvertiserDashboard,
    canViewAdvertiserRescues,
    getAdvertiserHomePath,
    isAdvertiserAttendant,
} from './utils/advertiserPermissions';
import AdvertiserSetupOnboarding from './pages/AdvertiserSetupOnboarding';
import { advertiserLayoutSx } from './advertiserLayoutSx';

const onboardingWidth = 400;

const menuItems = [
    { label: 'Visão geral', path: '/anunciante', allow: canViewAdvertiserDashboard },
    { label: 'Ofertas', path: '/anunciante/ofertas', allow: canManageAdvertiserOffers },
    { label: 'Usuários', path: '/anunciante/usuarios', allow: canManageAdvertiserUsers },
    { label: 'Resgates', path: '/anunciante/resgates', allow: canViewAdvertiserRescues },
    { label: 'Validar código', path: '/anunciante/validar', allow: canValidateAdvertiserRescues },
];

export default function AdvertiserProtected() {
    const location = useLocation();
    const navigate = useNavigate();
    const [{ advertiser: cookieAdvertiser }] = useCookies(['advertiser']);
    const advertiser = cookieAdvertiser || getStoredAdvertiser();
    const { authenticated, authReady, user, LogOut } = useAuth();
    const sessionUser = user || getStoredUser();
    const [onboardingActive, setOnboardingActive] = useState(false);
    const attendant = isAdvertiserAttendant(sessionUser);
    const homePath = getAdvertiserHomePath(sessionUser);

    const visibleMenuItems = useMemo(() => {
        return menuItems.filter(item => item.allow(sessionUser));
    }, [sessionUser]);

    if (!authReady || (authenticated && !sessionUser)) {
        return <LoadingContent />;
    }

    if (!authenticated) {
        return <Navigate to="/anunciante/login" replace />;
    }

    if (!isAdvertiserUser(sessionUser)) {
        return <Navigate to="/anunciante/login" replace />;
    }

    const contractPath = '/anunciante/contrato';
    const mustCompleteContract = !isAdvertiserContractSigned(advertiser);
    const canStayWhileUnsigned =
        location.pathname.startsWith('/anunciante/dados') ||
        (attendant && location.pathname.startsWith('/anunciante/validar'));

    // Atendente não assina contrato; só valida códigos.
    if (mustCompleteContract && !attendant && !canStayWhileUnsigned) {
        return <Navigate to={contractPath} replace />;
    }

    if (!canAccessAdvertiserPath(sessionUser, location.pathname)) {
        return <Navigate to={homePath} replace />;
    }

    return (
        <Box minHeight="100vh" display="flex" flexDirection="column">
            <AppBar position="fixed" elevation={1} color="primary">
                <Toolbar sx={{ gap: 2 }}>
                    <Box component="img" src={Logo} width={140} alt="Eurobem" />
                    <Typography variant="subtitle1" fontWeight="bold">
                        Área do anunciante
                    </Typography>
                    <List sx={{ display: { xs: 'none', md: 'flex' }, ml: 4 }}>
                        {visibleMenuItems.map(item => (
                            <ListItemButton
                                key={item.path}
                                component={Link}
                                to={item.path}
                                selected={
                                    item.path === '/anunciante'
                                        ? location.pathname === '/anunciante' || location.pathname === '/anunciante/'
                                        : location.pathname.startsWith(item.path)
                                }
                                sx={{ borderRadius: 2 }}
                            >
                                {item.label}
                            </ListItemButton>
                        ))}
                    </List>
                    <Box flexGrow={1} />
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'right', minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 180 }}>
                                {advertiser?.name || 'Anunciante'}
                            </Typography>
                            <Typography variant="caption" noWrap sx={{ display: 'block', opacity: 0.85, maxWidth: 180 }}>
                                {sessionUser?.userName}
                            </Typography>
                        </Box>
                        <Dropdown>
                            <IconButton
                                size="small"
                                color="inherit"
                                aria-label="Abrir menu do perfil"
                                sx={{
                                    p: 0.25,
                                    border: '2px solid',
                                    borderColor: 'rgba(255,255,255,0.55)',
                                    '&:hover': { borderColor: 'common.white' },
                                }}
                            >
                                <Avatar
                                    src={advertiser?.image || undefined}
                                    alt={advertiser?.name || 'Perfil'}
                                    sx={{
                                        width: 36,
                                        height: 36,
                                        bgcolor: 'rgba(255,255,255,0.22)',
                                        color: 'inherit',
                                        fontWeight: 700,
                                        fontSize: '0.95rem',
                                    }}
                                >
                                    {advertiser?.name?.[0]?.toUpperCase() || 'A'}
                                </Avatar>
                            </IconButton>
                            <Menu
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            >
                                <MenuItem disabled sx={{ opacity: '1 !important', py: 1.25 }}>
                                    <Stack spacing={0.25}>
                                        <Typography variant="subtitle2" fontWeight={700}>
                                            {advertiser?.name || 'Anunciante'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {sessionUser?.userName}
                                        </Typography>
                                    </Stack>
                                </MenuItem>
                                <Divider />
                                {canManageAdvertiserCompany(sessionUser) ? (
                                    <MenuItem component={Link} to="/anunciante/dados">
                                        Minha conta
                                    </MenuItem>
                                ) : null}
                                {!attendant ? (
                                    <MenuItem
                                        onClick={() => {
                                            localStorage.removeItem('advertiser_onboarding_completed');
                                            localStorage.setItem('advertiser_onboarding_reopen', 'true');
                                            window.dispatchEvent(new Event('storage'));
                                        }}
                                    >
                                        <RocketLaunch fontSize="small" sx={{ mr: 1 }} />
                                        Ver tutorial
                                    </MenuItem>
                                ) : null}
                                <MenuItem
                                    onClick={() => {
                                        LogOut('/anunciante');
                                        navigate('/', { replace: true });
                                    }}
                                >
                                    Sair
                                </MenuItem>
                            </Menu>
                        </Dropdown>
                    </Stack>
                </Toolbar>
            </AppBar>
            <Toolbar />
            <Box
                sx={{
                    flex: 1,
                    width: {
                        xs: '100%',
                        md: onboardingActive && !attendant ? `calc(100% - ${onboardingWidth}px)` : '100%',
                    },
                    marginRight: {
                        xs: 0,
                        md: onboardingActive && !attendant ? `${onboardingWidth}px` : 0,
                    },
                    transition: 'all 0.3s ease',
                }}
            >
                <Container maxWidth="xl" sx={{ py: 4, ...advertiserLayoutSx }}>
                    <Outlet context={{ advertiser }} />
                </Container>
            </Box>
            {!attendant ? <AdvertiserSetupOnboarding onOpenChange={setOnboardingActive} /> : null}
        </Box>
    );
}
